import React, { createContext, useContext, useState, useEffect, useCallback } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { ArrowLeftFromLine, ArrowRightFromLine, Plus } from "lucide-react"
import { useAppCtx, appStore } from "lib/state"
import { CollectionLoader, DeveloperInfoAccess, Modal } from "lib/components"
import { queryTools, cn, randoms, qCacheClient, useQCache } from "lib/utils"
import useTemplate from "lib/units/template/useTemplate"
import { Statement, StatementMarker, useStatements } from "."


/* ====== the refstr in props indicates if this if opening_statements or compelling_evidencen ====== */
export default function StatementEditingArea({ type }:{ type:string}){

  const { tmplid } = useParams()
  const [ searchParams, setSearchParams ] = useSearchParams() 

  const { appState } = useAppCtx()
  const { store, AppStore, refs }:any = appStore()
  const { get, setActive, setStore, update } = AppStore

  const [ activeStatements, setActiveStatements ] = useState([])
  const [ filteredStatements, setFilteredStatements ] = useState([])

  const { data:_Template } = queryTools.resource.parseQueryForApi("template.findById", { _id: tmplid })
  const { data:_Statements } = queryTools.resource.parseQueryForApi("statement.findAll", {})

  const saveTemplateFn = queryTools.resource.save(['template.one'], (data:any) => {
    update("randomizers.statements", randoms.string(8,"a#")) 
    return data
  })
  const saveStatementFn = queryTools.resource.save([`statement.all`, 'statement.one'])
  const removeStatementFn = queryTools.resource.save([`statement.all`, 'statement.one'])

  const { templateTools } = useTemplate(_Template)
  const { statementTools } = useStatements()

  const updateTemplateWithStatement = (newTmpl:any) => saveStatementFn.mutate({ resource: "statement", data: newTmpl })

  const filterStatements = (statements:any) => statements.filter( (st:any) => 
    st.parent.includes(store.statementLoadParams?.parent || "[root]")
    && st.level === store.statementLoadParams?.level || 1
    && st.parentResult === store.statementLoadParams?.parentResult || "any"
  )

  const handleCloneOperation = (statement:any) => {

  }



  // when the filtered statements change, we need to update the store with the new statements
  useEffect(() => {
    console.log("filtered statements updated", filteredStatements)
  }, [filteredStatements])

  useEffect(() => {
    if( searchParams.get("statement") ){
      const statements = _Template?.Elements.find( (elem:any) => elem.refstr === type)?.config.statements
      const found = statements?.find( (st:any) => st.refstr === searchParams.get("statement"))
      console.log(found)
      if( found ) update("actives.statement", found)
    } else {
      const elementStatements = _Template.Elements.find( (elem:any) => elem.refstr === store.element.refstr).config.statements
      const filtered = elementStatements.filter( (st:any) => 
        st.parent.includes(store.statementLoadParams?.parent || "[root]")
        && st.level === store.statementLoadParams?.level || 1
        && st.parentResult === store.statementLoadParams?.parentResult || "any"
      )
      setFilteredStatements(filtered)
    }
  },[_Template, searchParams.get("statement"), store.randomizers.statements])

  useEffect(() => {
    const elementTitle = (type === "opening_statements") ? "Opening Statements" : "Compelling Evidence"
    update("element.title", elementTitle)
  },[type])

  if( !store.element.title ) return <></>
  return (
    <div className="w-full h-full flex flex-col border border-solid border-slate-700 p-[2px] ">
      <div className="w-full h-8 flex justify-between items-center bg-slate-700 pl-6 pr-3 py-[2px] font-bold text-neutral-300 uppercase tracking-wider relative">
        
        <span className="cursor-pointer" onClick={() => update("actives.statement", null)}>{ store.element.title }</span>
        
        { store.actives.statement === null ? (
          <span
            className="h-full flex items-center bg-transparent border border-solid border-slate-600 cursor-pointer font-normal tracking-normal text-xs px-3" 
            onClick={() => statementTools.insert({ template: _Template, callbackFn: templateTools.statement.add })}
          >
            add new statement  
          </span>
        ):(
          <span
            className="h-full flex items-center bg-transparent border border-solid border-slate-600 cursor-pointer font-normal tracking-normal text-xs px-3" 
            onClick={() => update("actives.statement", null)}
          >
            return to list  
          </span>
        )}

      </div>
      
      { store.actives.statement === null && (
        <div className="w-full flex gap-4 px-3 py-3">
          <div className={cn("border border-solid border-slate-700 p-[2px]", store.metas.statementLoaderExpanded ? "w-3/12" : "w-[3rem]")}>
            { store.metas.statementLoaderExpanded === true ? (
              <CollectionLoader 
                title="Statement Loader"
                collection={_Statements || []} 
                labelProp="title" 
                showDescriptions={true}
                selectFn={(statement:any) => statementTools.insert({ template: _Template, statement, callbackFn: templateTools.statement.add })} 
                Icon={ <ArrowLeftFromLine className="size-4 cursor-pointer" onClick={() => update("metas.statementLoaderExpanded", false)} />}
              />
            ):(
              <div className="w-full h-full flex justify-center items-center">
                <ArrowRightFromLine className="size-4 cursor-pointer" onClick={() => update("metas.statementLoaderExpanded", true)}  />
              </div>
            )}
          </div>

          <div className={cn(store.metas.statementLoaderExpanded ? "w-9/12" : "w-[calc(100%-3rem)]")}>
            <div className="w-full flex flex-col">

              <div className="w-full flex flex-col gap-4">
                <div className="w-full flex flex-col gap-8">
                  { filteredStatements.map( (statementObj:any, idx:number) => (
                    <StatementMarker 
                      key={idx} 
                      template={_Template} 
                      statementObj={statementObj} 
                      saveTemplateFn={saveTemplateFn}
                      updateTemplateWithStatement={updateTemplateWithStatement}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div> 

      )}

      { store.actives.statement !== null && (
        <div className="w-full p-4">
          <Statement 
            template={_Template} 
            statementObj={store.actives.statement} 
            saveTemplateFn={saveTemplateFn}
            updateTemplateWithStatement={updateTemplateWithStatement}
          />
        </div>
      )}

      { store.actives.statement !== null && ( 
       <div className="w-full h-6 flex justify-center items-center bg-teal-800 text-neutral-300/70 uppercase text-xs">
          <span onClick={() => saveStatementFn.mutate({ resource: "statement", data: store.actives.statement })}>save entire statement for re-use</span>
        </div> 
      )}
    </div>
  )
}